import type { AxiosError } from 'axios';
import { authClient } from './client';
import type {
  ProductSaleRow,
  ProductSalesListPayload,
  ProductSalesListQuery,
  SaleChannel,
  TransactionStatus,
  TransactionType,
} from '../types/productSales';
import { TRANSACTION_STATUS_OPTIONS, TRANSACTION_TYPE_OPTIONS } from '../types/productSales';

// ─── Parsers ──────────────────────────────────────────────────────────────────

function parseStatus(raw: unknown): TransactionStatus | null {
  const s = String(raw ?? '')
    .trim()
    .toUpperCase();
  return (TRANSACTION_STATUS_OPTIONS as string[]).includes(s) ? (s as TransactionStatus) : null;
}

function parseType(raw: unknown): TransactionType | null {
  const s = String(raw ?? '')
    .trim()
    .toUpperCase();
  return (TRANSACTION_TYPE_OPTIONS as string[]).includes(s) ? (s as TransactionType) : null;
}

function parseChannel(raw: unknown): SaleChannel {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (s === 'shopify') return 'shopify';
  if (s === 'in-store' || s === 'instore' || s === 'in_store') return 'in-store';
  return null;
}

function parseProductSaleRow(raw: unknown): ProductSaleRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const transactionId = Number(r.transactionId ?? r.id);
  const variantId = Number(r.variantId);
  if (!Number.isFinite(transactionId) || !Number.isFinite(variantId)) return null;
  const status = parseStatus(r.status);
  const type = parseType(r.type);
  if (!status || !type) return null;
  const artIdRaw = r.artId ?? r.art_id;
  const shopifySaleIdRaw = r.shopifySaleId ?? r.shopify_sale_id ?? r.shopifyOrderId;
  const imageUrlRaw = r.imageUrl ?? r.image_url ?? r.thumbnailUrl;
  return {
    transactionId,
    transactionCreatedAt: (() => {
      if (typeof r.transactionCreatedAt === 'string' && r.transactionCreatedAt)
        return r.transactionCreatedAt;
      if (typeof r.createdAt === 'string' && r.createdAt) return r.createdAt;
      return new Date(0).toISOString();
    })(),
    productName: String(r.productName ?? r.product_name ?? '').trim(),
    imageUrl: typeof imageUrlRaw === 'string' && imageUrlRaw.trim() ? imageUrlRaw.trim() : null,
    artId: artIdRaw != null && String(artIdRaw).trim() ? String(artIdRaw).trim() : null,
    variantId,
    variantName: String(r.variantName ?? r.variant_name ?? '').trim(),
    type,
    status,
    channel: parseChannel(r.channel),
    shopifySaleId:
      shopifySaleIdRaw != null && String(shopifySaleIdRaw).trim()
        ? String(shopifySaleIdRaw).trim()
        : null,
  };
}

function parseListPayload(
  body: unknown,
  fallbackPage: number,
  fallbackPageSize: number
): ProductSalesListPayload {
  const empty: ProductSalesListPayload = {
    items: [],
    total: 0,
    page: fallbackPage,
    pageSize: fallbackPageSize,
  };
  if (!body || typeof body !== 'object') return empty;
  const o = body as Record<string, unknown>;
  const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
    string,
    unknown
  >;
  const rawItems = inner.items;
  const items = Array.isArray(rawItems)
    ? rawItems.map(parseProductSaleRow).filter((x): x is ProductSaleRow => x !== null)
    : [];
  const total =
    typeof inner.total === 'number' && Number.isFinite(inner.total) ? inner.total : items.length;
  const page = typeof inner.page === 'number' && inner.page >= 1 ? inner.page : fallbackPage;
  const pageSize =
    typeof inner.pageSize === 'number' && inner.pageSize >= 1 ? inner.pageSize : fallbackPageSize;
  return { items, total, page, pageSize };
}

// ─── Exported function ────────────────────────────────────────────────────────

export type ProductSalesListResult =
  | { ok: true; data: ProductSalesListPayload }
  | { ok: false; error: string };

export async function fetchProductSales(
  query: ProductSalesListQuery = {}
): Promise<ProductSalesListResult> {
  const page = query.page ?? 1;
  const pageSize = query.pageSize ?? 25;
  try {
    const params: Record<string, string | number> = { page, pageSize };
    if (query.productName?.trim()) params.productName = query.productName.trim();
    if (query.status) params.status = query.status;
    if (query.type) params.type = query.type;
    if (query.shopifySaleId?.trim()) params.shopifySaleId = query.shopifySaleId.trim();
    if (query.variantSearch?.trim()) params.variantSearch = query.variantSearch.trim();
    const { data } = await authClient.get<unknown>('/api/admin/product-sales', { params });
    return { ok: true, data: parseListPayload(data, page, pageSize) };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404 ? 'El endpoint de ventas aún no está disponible.' : null) ??
      'No se pudo cargar el historial de ventas.';
    return { ok: false, error: msg };
  }
}
